import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Button,
  Space,
  message,
  Spin,
  Typography,
  Tag,
  Row,
  Col,
  Alert,
  Avatar,
  Skeleton,
} from 'antd';
import {
  CalendarOutlined,
  CheckCircleOutlined,
  GiftOutlined,
  FireOutlined,
  ClockCircleOutlined,
  SmileOutlined,
} from '@ant-design/icons';
import llmService from '../../services/llmService';

const { Text, Title } = Typography;

// 配额转换为美元
const quotaToDollar = (quota) => {
  if (quota === undefined || quota === null) return '$0.00';
  const dollars = quota / 500000;
  if (dollars >= 0.01) return `$${dollars.toFixed(2)}`;
  return `$${dollars.toFixed(4)}`;
};

const Checkin = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkinLoading, setCheckinLoading] = useState(false);
  const [checkedIn, setCheckedIn] = useState(false);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const response = await llmService.getCheckinStatus();
      if (response.success) {
        setStatus(response.data);
        setCheckedIn(response.data?.is_checked_in || false);
      }
    } catch (error) {
      // Checkin may not be enabled
      if (error?.error?.includes('未启用') || error?.message?.includes('未启用')) {
        setStatus({ disabled: true });
      } else {
        message.error(t('Failed to retrieve check-in status'));
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleCheckin = async () => {
    setCheckinLoading(true);
    try {
      const response = await llmService.doCheckin();
      if (response.success) {
        message.success(response.message || t('Checked in successfully!'));
        setCheckedIn(true);
        fetchStatus();
      } else {
        message.error(response.error || t('Check-in failed'));
      }
    } catch (error) {
      message.error(error?.error || error?.message || t('Check-in failed'));
    } finally {
      setCheckinLoading(false);
    }
  };

  if (loading && !status) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (status?.disabled) {
    return (
      <Card style={{ borderRadius: 12 }}>
        <Alert
          message={t('Check-in is not enabled')}
          description={t('The administrator has not enabled check-in yet. Please contact the administrator to enable it.')}
          type="info"
          showIcon
          icon={<CalendarOutlined />}
        />
      </Card>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Text style={{ fontSize: 20, fontWeight: 600 }}>
          <CalendarOutlined style={{ marginRight: 8 }} />
          {t('Daily check-in')}
        </Text>
      </div>

      <Row gutter={[16, 16]}>
        {/* 签到卡片 */}
        <Col xs={24} md={12}>
          <Card
            style={{
              borderRadius: 12,
              background: checkedIn
                ? 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)'
                : 'linear-gradient(135deg, #e6f4ff 0%, #bae0ff 100%)',
              textAlign: 'center',
              minHeight: 280,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            styles={{ body: { width: '100%' } }}
          >
            <Avatar
              size={80}
              style={{
                backgroundColor: checkedIn ? '#52c41a' : '#1890ff',
                marginBottom: 16,
              }}
              icon={checkedIn ? <CheckCircleOutlined /> : <CalendarOutlined />}
            />
            <Title level={4} style={{ marginBottom: 8 }}>
              {checkedIn ? t('Checked in today') : t('Not checked in today')}
            </Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
              {checkedIn
                ? t('Check in again tomorrow for more rewards!')
                : t('Click the button below to check in today')}
            </Text>
            <Button
              type="primary"
              size="large"
              icon={checkedIn ? <CheckCircleOutlined /> : <GiftOutlined />}
              onClick={handleCheckin}
              loading={checkinLoading}
              disabled={checkedIn}
              style={{
                borderRadius: 8,
                height: 44,
                paddingInline: 32,
                ...(checkedIn ? {} : {
                  background: 'linear-gradient(135deg, #1890ff, #722ed1)',
                  border: 'none',
                }),
              }}
            >
              {checkedIn ? t('Checked in') : t('Check in now')}
            </Button>
          </Card>
        </Col>

        {/* 签到信息 */}
        <Col xs={24} md={12}>
          <Card
            title={
              <Space>
                <FireOutlined style={{ color: '#fa8c16' }} />
                <span>{t('Check-in details')}</span>
              </Space>
            }
            style={{ borderRadius: 12, minHeight: 280 }}
          >
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Space>
                  <Avatar size="small" style={{ backgroundColor: '#fa8c16' }} icon={<FireOutlined />} />
                  <Text>{t('Check-in streak')}</Text>
                </Space>
                <Text strong style={{ fontSize: 18 }}>
                  {t('{{days}} days', { days: status?.continuous_checkin_count || status?.streak || 0 })}
                </Text>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Space>
                  <Avatar size="small" style={{ backgroundColor: '#1890ff' }} icon={<CalendarOutlined />} />
                  <Text>{t('Total check-in days')}</Text>
                </Space>
                <Text strong style={{ fontSize: 18 }}>
                  {t('{{days}} days', { days: status?.total_checkin_count || status?.total || 0 })}
                </Text>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Space>
                  <Avatar size="small" style={{ backgroundColor: '#52c41a' }} icon={<GiftOutlined />} />
                  <Text>{t('Check-in reward quota')}</Text>
                </Space>
                <Text strong style={{ fontSize: 18, color: '#52c41a' }}>
                  {quotaToDollar(status?.checkin_quota || status?.reward || 0)}
                </Text>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Space>
                  <Avatar size="small" style={{ backgroundColor: '#722ed1' }} icon={<ClockCircleOutlined />} />
                  <Text>{t('Last check-in')}</Text>
                </Space>
                <Text type="secondary">
                  {status?.last_checkin_time
                    ? new Date(status.last_checkin_time * 1000).toLocaleString('zh-CN')
                    : t('No records yet')}
                </Text>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* 签到说明 */}
      <Card
        style={{ borderRadius: 12, marginTop: 16 }}
        title={
          <Space>
            <SmileOutlined style={{ color: '#1890ff' }} />
            <span>{t('About check-in')}</span>
          </Space>
        }
      >
        <ul style={{ margin: 0, paddingLeft: 20, color: '#666' }}>
          <li>{t('Earn free quota rewards with daily check-in')}</li>
          <li>{t('Longer check-in streaks may earn greater rewards')}</li>
          <li>{t('You can check in again after 00:00 each day')}</li>
          <li>{t('Quota earned from check-in will be added to your account automatically')}</li>
        </ul>
      </Card>
    </div>
  );
};

export default Checkin;
